/**
 * The single definition of the public deep links into a game (FR-2.9, FR-9.10).
 *
 * A player reaches a game by opening `/player?code=<joinCode>`, which the player
 * page reads to prefill (and, for a registered user, auto-submit) the join form.
 * A shared screen reaches it by opening `/presentation?code=<joinCode>`, which the
 * presentation page reads to auto-attach to that game (#51). These URLs are what the
 * host banner shows, what the copy-link button copies, and what the shareable QR code
 * encodes — one helper each so those uses never drift.
 *
 * The payload is a PUBLIC deep link: the join code is meant to be shared (it is
 * unguessable per NFR-5.2, but not a secret). No PII is encoded.
 */

/** The current page origin, guarded for non-browser (test) environments. */
const currentOrigin = (): string =>
  typeof location !== 'undefined' ? location.origin : '';

/**
 * Build the shareable player-join URL for a game's join code.
 *
 * @param joinCode the game's join code (raw, as shown to the host).
 * @param origin   the site origin to build against; defaults to the current
 *                 page origin, guarded for non-browser (test) environments where
 *                 `location` is absent.
 */
export function playerJoinUrl(
  joinCode: string,
  origin: string = currentOrigin(),
): string {
  return `${origin}/player?code=${encodeURIComponent(joinCode)}`;
}

/**
 * Build the shareable presentation (shared-screen / TV) deep link for a game's join
 * code (#51, FR-9.10). Opening it auto-attaches the presentation view to THAT game.
 *
 * @param joinCode the game's join code (raw, as shown to the host).
 * @param origin   the site origin to build against; defaults to the current page origin.
 */
export function presentationUrl(
  joinCode: string,
  origin: string = currentOrigin(),
): string {
  return `${origin}/presentation?code=${encodeURIComponent(joinCode)}`;
}
